import { parsePromqlExpression } from "./parsePromqlExpression";

function findMatchingParen(str, openIdx) {
  let depth = 0;
  for (let i = openIdx; i < str.length; i += 1) {
    const ch = str[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function parseLabelPairs(inner) {
  const pairs = [];
  let pos = 0;
  while (pos < inner.length) {
    const keyMatch = inner.slice(pos).match(
      /^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(=|!=|=~|!~)\s*"((?:[^"\\]|\\.)*)"\s*/
    );
    if (!keyMatch) break;
    pairs.push({
      key: keyMatch[1],
      op: keyMatch[2],
      value: keyMatch[3].replace(/\\(.)/g, "$1"),
    });
    pos += keyMatch[0].length;
    if (inner.slice(pos).trimStart().startsWith(",")) {
      pos = inner.indexOf(",", pos) + 1;
    } else {
      break;
    }
  }
  return pairs;
}

const LOKI_RANGE_FUNCS = new Set([
  "rate", "rate_counter", "count_over_time", "sum_over_time", "avg_over_time",
  "min_over_time", "max_over_time", "first_over_time", "last_over_time",
  "stddev_over_time", "stdvar_over_time", "quantile_over_time", "bytes_rate",
  "bytes_over_time", "absent_over_time",
]);

const LOKI_AGG_FUNCS = new Set([
  "sum", "avg", "min", "max", "count", "stddev", "stdvar", "topk", "bottomk",
]);

const BINARY_OPS = [">=", "<=", "==", "!=", ">", "<"];

function stripOuterParens(expr) {
  let trimmed = expr.trim();
  while (trimmed.startsWith("(") && trimmed.endsWith(")")) {
    const closeIdx = findMatchingParen(trimmed, 0);
    if (closeIdx !== trimmed.length - 1) break;
    trimmed = trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function peelLokiOperation(expr) {
  const trimmed = expr.trim();
  if (!trimmed) return null;

  const byMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s+by\s*\(([^)]*)\)\s*\(/);
  if (byMatch && LOKI_AGG_FUNCS.has(byMatch[1])) {
    const opId = byMatch[1];
    const byKeywordIdx = trimmed.indexOf("by");
    const byOpen = trimmed.indexOf("(", byKeywordIdx);
    const byClose = findMatchingParen(trimmed, byOpen);
    if (byClose === -1) return null;
    const byLabels = trimmed
      .slice(byOpen + 1, byClose)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const rest = trimmed.slice(byClose + 1).trim();
    if (!rest.startsWith("(")) return null;
    const innerClose = findMatchingParen(rest, 0);
    if (innerClose === -1) return null;
    return {
      op: { id: opId, display: opId, params: { by: byLabels } },
      inner: rest.slice(1, innerClose).trim(),
    };
  }

  const funcMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\(/);
  if (!funcMatch) return null;

  const opId = funcMatch[1];
  const openIdx = funcMatch[0].length - 1;
  const closeIdx = findMatchingParen(trimmed, openIdx);
  if (closeIdx === -1) return null;
  const argsContent = trimmed.slice(openIdx + 1, closeIdx).trim();

  if (LOKI_RANGE_FUNCS.has(opId)) {
    if (opId === "quantile_over_time") {
      const commaIdx = argsContent.indexOf(",");
      if (commaIdx > -1) {
        const q = argsContent.slice(0, commaIdx).trim();
        const rest = argsContent.slice(commaIdx + 1).trim();
        const rangeMatch = rest.match(/^(.+)\[([^\]]+)\]$/s);
        if (rangeMatch) {
          return {
            op: { id: opId, display: opId, params: { q: parseFloat(q), range: rangeMatch[2].trim() } },
            inner: rangeMatch[1].trim(),
          };
        }
      }
    }
    const rangeMatch = argsContent.match(/^(.+)\[([^\]]+)\]$/s);
    if (rangeMatch) {
      return {
        op: { id: opId, display: opId, params: { range: rangeMatch[2].trim() } },
        inner: rangeMatch[1].trim(),
      };
    }
  }

  if (opId === "topk" || opId === "bottomk") {
    const commaIdx = argsContent.indexOf(",");
    if (commaIdx > -1) {
      return {
        op: { id: opId, display: opId, params: { k: parseFloat(argsContent.slice(0, commaIdx).trim()) } },
        inner: argsContent.slice(commaIdx + 1).trim(),
      };
    }
  }

  if (LOKI_AGG_FUNCS.has(opId) || LOKI_RANGE_FUNCS.has(opId)) {
    return {
      op: { id: opId, display: opId, params: {} },
      inner: argsContent,
    };
  }

  return null;
}

function peelBinaryOperator(expr) {
  const trimmed = expr.trim();
  if (!trimmed) return null;

  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = trimmed.length - 1; i >= 0; i -= 1) {
    const ch = trimmed[i];

    if (escape) {
      escape = false;
      continue;
    }
    if (ch === "\\" && inString) {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (ch === ")") depth += 1;
    else if (ch === "(") depth -= 1;
    else if (depth === 0) {
      for (const op of BINARY_OPS) {
        const opStart = i - op.length + 1;
        if (opStart < 0) continue;
        if (trimmed.slice(opStart, i + 1) !== op) continue;

        const left = trimmed.slice(0, opStart).trim();
        const right = trimmed.slice(i + 1).trim();
        if (!left || !right) continue;

        return {
          op: { id: op, display: op, params: { value: right } },
          inner: stripOuterParens(left),
        };
      }
    }
  }
  return null;
}

function peelOneLayer(expr) {
  return peelLokiOperation(expr) || peelBinaryOperator(expr);
}

function parseLokiCore(expr) {
  const trimmed = expr.trim();
  const selectorMatch = trimmed.match(/\{([^}]+)\}/);
  const labels = selectorMatch
    ? parseLabelPairs(selectorMatch[1].trim()).map((p) => ({ key: p.key, op: p.op, value: p.value }))
    : [{ key: "", op: "=", value: "" }];

  const lineFilters = [];
  const lineRegex = /\s(\|=|!=|!~|=~|~)\s*"((?:[^"\\]|\\.)*)"/g;
  let match;
  while ((match = lineRegex.exec(trimmed)) !== null) {
    lineFilters.push({ type: match[1], value: match[2].replace(/\\(.)/g, "$1") });
  }

  return {
    labels: labels.length ? labels : [{ key: "", op: "=", value: "" }],
    lineFilters,
  };
}

export function parseLogqlExpression(expression) {
  if (!expression || typeof expression !== "string") {
    return { labels: [{ key: "", op: "=", value: "" }], lineFilters: [], ops: [] };
  }

  let current = expression.trim();
  const ops = [];

  while (current) {
    const peeled = peelOneLayer(current);
    if (!peeled?.inner) break;
    ops.unshift(peeled.op);
    current = peeled.inner.trim();
  }

  const core = parseLokiCore(current);
  return { ...core, ops };
}

export function parseBuilderExpression(expression, engine = "prometheus") {
  if (engine === "loki") return parseLogqlExpression(expression);
  return parsePromqlExpression(expression);
}

export default parseLogqlExpression;
